/**
 * Shared class hooks for the light-bar text fields on the Rewards Destinations
 * and Add a Position flows: the design system's `InputGroup` /
 * `ComboboxInput` (both draw the lightbar rail on the group shell), with the
 * label inside the field: an inline-start `InputGroupAddon` with a divider
 * holding `InputGroupText` (the DS Input "Prefix and suffix" recipe).
 */

/**
 * The form measure for these fields. The label sits inside the field, and a
 * full address (44 characters in the numeric face) still has to fit after
 * it, so the column sits at `max-w-2xl` (42rem) rather than the console's
 * 36rem `max-w-xl`, and stacked fields keep one right edge.
 */
export const LIGHTBAR_FIELD_MEASURE = "w-full max-w-2xl";

/**
 * How a read-only field looks disabled without being disabled, on a DS
 * `InputGroup` (the group shell owns the rail). The same approach as the
 * Distributor Console's `READ_ONLY_INPUT_CLASS`
 * (yaas-issuer `pages/Clients/ReadOnlyField.tsx`): the DS has no read-only
 * state and its disabled one fails AA and can't be selected or copied, so
 * the field stays `readOnly` and borrows the DS disabled rail token:
 *
 * - rail: `line-neutral`, the DS disabled rail;
 * - value: `foreground-neutral`, 5.9:1 on Paper;
 * - no ember hover wipe, default pointer;
 * - keyboard focus still shows the DS focus rail.
 *
 * Put it on the `InputGroup`.
 */
export const READ_ONLY_GROUP_CLASS =
    // why: arbitrary variants reach the group's own rail pseudo-elements and
    // its control's data-slot; `:not(:has(:focus-visible))` outranks the
    // rail's resting colour without `!` and leaves the focus rail alone.
    "[&:not(:has(:focus-visible))]:before:bg-line-neutral after:hidden [&_[data-slot=input-group-control]]:text-foreground-neutral [&_[data-slot=input-group-control]]:cursor-default";

/**
 * A disabled light-bar field keeps the DS disabled rail (`line-neutral`, no
 * hover) but lifts the value from `foreground-imprint` (3.4:1, fails AA) to
 * `foreground-neutral` (5.9:1), matching the read-only look above.
 */
export const DISABLED_VALUE_CLASS = "disabled:text-foreground-neutral";

/**
 * The DS `outline` interaction on a label-inside field. The DS `Input` has
 * `interaction="outline"`, but `InputGroup` (the only shell with an inline
 * label) always draws the light-bar rail, so this hides the rail and draws the
 * same box border the outline `Input` uses: `line-base` idle, charcoal-700 on
 * hover, charcoal-500 on focus, 80ms in / 200ms out. Put it on the
 * `InputGroup`. Candidate for the DS: an `interaction` prop on `InputGroup`.
 */
export const OUTLINE_GROUP_CLASS =
    // why: the group has no outline variant; these mirror the DS Input's
    // OUTLINE_CLASSES on the group shell and switch its rail pseudo-elements off.
    "before:hidden after:hidden border border-line-base hover:border-charcoal-700 has-[:focus-visible]:border-charcoal-500 duration-200 ease-[var(--ease-in)] hover:duration-[80ms] hover:ease-[var(--ease-out)] has-[:focus-visible]:duration-[80ms] has-[:focus-visible]:ease-[var(--ease-out)]";
